import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, Send, CheckCircle, AlertCircle, Mail, Phone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TestimonialForm({ onClose, user }) {
  const [text, setText] = useState('');
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [userName, setUserName] = useState(user?.full_name || user?.name || '');
  const [userRole, setUserRole] = useState('');
  // Guest fields (only when not logged in)
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [status, setStatus] = useState(null); // null | 'submitting' | 'success' | 'error' | 'exists'
  const [errorMsg, setErrorMsg] = useState('');
  const [existing, setExisting] = useState(null);

  const isGuest = !user;

  useEffect(() => {
    if (isGuest) return;
    const check = async () => {
      try {
        const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        if (!token) return;
        const res = await fetch(`${API}/api/testimonials/my`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.testimonial_id) setExisting(data);
        }
      } catch { /* silent */ }
    };
    check();
  }, [isGuest]);

  const handleSubmit = async () => {
    if (text.trim().length < 20) { setErrorMsg('En az 20 karakter yazın.'); return; }
    if (isGuest) {
      if (!guestEmail || !guestEmail.includes('@')) {
        setErrorMsg('Geçerli bir e-posta adresi giriniz.');
        return;
      }
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      if (isGuest) {
        // Guest submission
        const res = await fetch(`${API}/api/testimonials/guest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: text.trim(),
            rating,
            user_name: userName || 'Guest',
            user_role: userRole,
            email: guestEmail.trim().toLowerCase(),
            phone: guestPhone.trim(),
          }),
        });
        if (res.status === 409) { setStatus('exists'); return; }
        if (!res.ok) {
          const d = await res.json();
          setErrorMsg(d.detail || 'Bir hata oluştu.');
          setStatus('error');
          return;
        }
      } else {
        // Auth submission
        const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        const res = await fetch(`${API}/api/testimonials`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ text: text.trim(), rating, user_name: userName, user_role: userRole }),
        });
        if (res.status === 409) { setStatus('exists'); return; }
        if (!res.ok) {
          const d = await res.json();
          setErrorMsg(d.detail || 'Bir hata oluştu.');
          setStatus('error');
          return;
        }
      }
      setStatus('success');
    } catch {
      setErrorMsg('Bağlantı hatası. Lütfen tekrar deneyin.');
      setStatus('error');
    }
  };

  const statusMap = {
    pending: { label: 'İnceleme Bekliyor', color: 'yellow' },
    approved: { label: 'Onaylandı', color: 'emerald' },
    featured: { label: 'Öne Çıkarıldı ⭐', color: 'orange' },
    rejected: { label: 'Reddedildi', color: 'red' },
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">

        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-orange-50 to-amber-50">
          <div>
            <h2 className="text-base font-bold text-gray-900">Deneyiminizi Paylaşın</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {isGuest ? 'Giriş yapmadan yorum gönderebilirsiniz' : 'Yorumunuz incelendikten sonra yayınlanır'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6">
          <AnimatePresence mode="wait">

            {/* Zaten gönderilmiş */}
            {existing && !status && (
              <motion.div key="existing" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="text-center space-y-4">
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold
                  ${existing.status === 'approved' || existing.status === 'featured' ? 'bg-emerald-50 text-emerald-700' :
                    existing.status === 'pending' ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700'}`}>
                  <CheckCircle className="w-4 h-4" />
                  {statusMap[existing.status]?.label || existing.status}
                </div>
                <blockquote className="text-sm text-gray-600 italic bg-gray-50 rounded-xl p-4">
                  "{existing.text}"
                </blockquote>
                {existing.status === 'rejected' && (
                  <p className="text-xs text-red-500">Yorumunuz reddedildi. Yeni bir yorum göndermek için destek ile iletişime geçin.</p>
                )}
                <button onClick={onClose} className="w-full py-2.5 text-sm font-semibold bg-gray-100 hover:bg-gray-200 rounded-xl">Kapat</button>
              </motion.div>
            )}

            {/* Başarı */}
            {status === 'success' && (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                className="text-center space-y-4 py-4">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-base">Yorumunuz Gönderildi!</p>
                  <p className="text-sm text-gray-500 mt-1">Ekibimiz inceledikten sonra ana sayfada yayınlanacak.</p>
                </div>
                <button onClick={onClose} className="w-full py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl">Tamam</button>
              </motion.div>
            )}

            {/* Form */}
            {!existing && status !== 'success' && (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                {/* Guest bilgi bandı */}
                {isGuest && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-700">
                      Giriş yapmadan yorum gönderebilirsiniz. E-posta adresiniz sadece bildirim için kullanılır ve herkese açık gösterilmez.
                    </p>
                  </div>
                )}

                {/* Puan */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-2">Puanınız</label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(s => (
                      <button key={s}
                        onClick={() => setRating(s)}
                        onMouseEnter={() => setHoverRating(s)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="p-1 transition-transform hover:scale-110">
                        <Star className={`w-7 h-7 transition-colors ${s <= (hoverRating || rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Ad & Rol */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Adınız / Şirketiniz</label>
                    <input value={userName} onChange={e => setUserName(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                      placeholder="Şirket ya da adınız" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Rolünüz / Pozisyon</label>
                    <input value={userRole} onChange={e => setUserRole(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                      placeholder="ör. Marketing Director" />
                  </div>
                </div>

                {/* Guest: Email (zorunlu) + Telefon (isteğe bağlı) */}
                {isGuest && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 block mb-1">
                        E-posta <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        <input
                          type="email"
                          value={guestEmail}
                          onChange={e => { setGuestEmail(e.target.value); setErrorMsg(''); }}
                          className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                          placeholder="ornek@email.com"
                          data-testid="guest-email-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 block mb-1">
                        Telefon <span className="text-gray-400 font-normal">(isteğe bağlı)</span>
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                        <input
                          type="tel"
                          value={guestPhone}
                          onChange={e => setGuestPhone(e.target.value)}
                          className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                          placeholder="+90 5xx xxx xx xx"
                          data-testid="guest-phone-input"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Metin */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">
                    Yorumunuz <span className="text-gray-400">({text.length}/800)</span>
                  </label>
                  <textarea
                    value={text}
                    onChange={e => { setText(e.target.value); setErrorMsg(''); }}
                    rows={5}
                    maxLength={800}
                    placeholder="ExpoStand platformunu nasıl kullandınız? Deneyiminizi paylaşın..."
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-400 leading-relaxed"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">En az 20 karakter · Yorum incelendikten sonra yayınlanır</p>
                </div>

                {errorMsg && (
                  <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {errorMsg}
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={status === 'submitting' || text.trim().length < 20}
                  data-testid="testimonial-submit-btn"
                  className="w-full flex items-center justify-center gap-2 py-3 text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
                  {status === 'submitting'
                    ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Gönderiliyor...</>
                    : <><Send className="w-4 h-4" /> Yorum Gönder</>}
                </button>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
